import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle, 
  CardFooter 
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Banknote, PiggyBank, FileText, ArrowRight } from 'lucide-react';
import { format } from 'date-fns';
import { useToast } from "@/components/ui/use-toast";

interface InvestmentOperationsProps {
  initialAmount?: number;
  timeHorizon?: string;
}

const InvestmentOperations: React.FC<InvestmentOperationsProps> = ({ initialAmount, timeHorizon }) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("new");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showRedeemDialog, setShowRedeemDialog] = useState(false);
  const [operationSuccess, setOperationSuccess] = useState(false);

  const [newInvestment, setNewInvestment] = useState({
    type: "fd",
    amount: initialAmount ? initialAmount : 10000000,
    institution: "",
    interestRate: 6.25,
    tenor: 3, // in months for FD
    startDate: format(new Date(), 'yyyy-MM-dd'),
    maturityInstructions: "principal_and_interest"
  });

  // Update investment amount when initialAmount prop changes
  useEffect(() => {
    if (initialAmount) {
      setNewInvestment(prev => ({
        ...prev,
        amount: initialAmount
      }));
    }
  }, [initialAmount]);

  const [redemption, setRedemption] = useState({
    id: "",
    amount: 0,
    investmentType: "fd",
    redemptionDate: format(new Date(), 'yyyy-MM-dd'),
    reason: "liquidity_needs",
    penalties: 0
  });

  // Sample investments for the redemption options
  const availableInvestments = [
    { id: "inv1", type: "Fixed Deposit", institution: "HDFC Bank", principal: 25000000, maturityDate: "2025-08-30" },
    { id: "inv2", type: "Fixed Deposit", institution: "SBI", principal: 30000000, maturityDate: "2025-10-15" },
    { id: "inv3", type: "Liquid Mutual Fund", institution: "SBI Liquid Fund", principal: 17500000, maturityDate: null },
    { id: "inv4", type: "Liquid Mutual Fund", institution: "HDFC Liquid Fund", principal: 15000000, maturityDate: null },
  ];

  const banks = ["HDFC Bank", "SBI", "ICICI Bank", "Axis Bank", "Kotak Bank", "Yes Bank", "IndusInd Bank"];
  const mutualFunds = ["SBI Liquid Fund", "HDFC Liquid Fund", "Aditya Birla Sun Life Liquid Fund", "ICICI Prudential Liquid Fund", "Kotak Liquid Fund"];

  const handleNewInvestment = () => {
    setShowConfirmDialog(true);
  };

  const confirmInvestment = () => {
    setShowConfirmDialog(false);
    setOperationSuccess(true);
    
    toast({
      title: "Investment Created",
      description: `New ${newInvestment.type === 'fd' ? 'Fixed Deposit' : 'Liquid Fund'} investment of ₹${(newInvestment.amount / 100000).toFixed(2)} lakhs in ${newInvestment.institution} has been initiated.`,
    });
    
    // Reset form after 1.5 seconds
    setTimeout(() => {
      setOperationSuccess(false);
      setNewInvestment({
        type: "fd",
        amount: 10000000,
        institution: "",
        interestRate: 6.25,
        tenor: 3,
        startDate: format(new Date(), 'yyyy-MM-dd'),
        maturityInstructions: "principal_and_interest"
      });
    }, 1500);
  };

  const handleRedemptionRequest = () => {
    setShowRedeemDialog(true);
  };

  const confirmRedemption = () => {
    setShowRedeemDialog(false);
    setOperationSuccess(true);
    
    toast({
      title: "Redemption Initiated",
      description: `Redemption request for investment in ${availableInvestments.find(inv => inv.id === redemption.id)?.institution} has been initiated.`,
    });
    
    // Reset form after 1.5 seconds
    setTimeout(() => {
      setOperationSuccess(false);
      setRedemption({
        id: "",
        amount: 0,
        investmentType: "fd",
        redemptionDate: format(new Date(), 'yyyy-MM-dd'),
        reason: "liquidity_needs",
        penalties: 0
      });
    }, 1500);
  };

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="new">New Investment</TabsTrigger>
          <TabsTrigger value="redeem">Redemption</TabsTrigger>
        </TabsList>
        <TabsContent value="new" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle>Create New Investment</CardTitle>
              <CardDescription>
                Deploy surplus funds in Fixed Deposits or Liquid Mutual Funds
                {timeHorizon && <span className="block mt-1 text-sm font-medium">Suggested horizon: {timeHorizon}</span>}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Investment Type</Label>
                  <RadioGroup 
                    defaultValue={newInvestment.type} 
                    className="flex space-x-4"
                    onValueChange={(value) => setNewInvestment({...newInvestment, type: value})}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="fd" id="fd" />
                      <Label htmlFor="fd" className="flex items-center">
                        <Banknote className="mr-1.5 h-4 w-4" />
                        Fixed Deposit
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="mf" id="mf" />
                      <Label htmlFor="mf" className="flex items-center">
                        <PiggyBank className="mr-1.5 h-4 w-4" />
                        Liquid Mutual Fund
                      </Label>
                    </div>
                  </RadioGroup>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="amount">Investment Amount (₹)</Label>
                  <Input 
                    id="amount" 
                    type="number" 
                    value={newInvestment.amount} 
                    onChange={(e) => setNewInvestment({...newInvestment, amount: parseFloat(e.target.value)})}
                  />
                  <p className="text-sm text-muted-foreground">
                    {`₹${(newInvestment.amount / 100000).toFixed(2)} lakhs`}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="institution">
                    {newInvestment.type === 'fd' ? 'Bank' : 'Mutual Fund'}
                  </Label>
                  <Select 
                    value={newInvestment.institution}
                    onValueChange={(value) => setNewInvestment({...newInvestment, institution: value})}
                  >
                    <SelectTrigger id="institution">
                      <SelectValue placeholder={`Select ${newInvestment.type === 'fd' ? 'bank' : 'fund'}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {(newInvestment.type === 'fd' ? banks : mutualFunds).map((institution) => (
                        <SelectItem key={institution} value={institution}>{institution}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {newInvestment.type === 'fd' && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="interestRate">Interest Rate (%)</Label>
                      <Input 
                        id="interestRate" 
                        type="number" 
                        step="0.01"
                        value={newInvestment.interestRate} 
                        onChange={(e) => setNewInvestment({...newInvestment, interestRate: parseFloat(e.target.value)})}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="tenor">Tenor (months)</Label>
                      <Select 
                        value={String(newInvestment.tenor)}
                        onValueChange={(value) => setNewInvestment({...newInvestment, tenor: parseInt(value)})}
                      >
                        <SelectTrigger id="tenor">
                          <SelectValue placeholder="Select tenor" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 month</SelectItem>
                          <SelectItem value="3">3 months</SelectItem>
                          <SelectItem value="6">6 months</SelectItem>
                          <SelectItem value="9">9 months</SelectItem>
                          <SelectItem value="12">12 months</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="maturityInstructions">Maturity Instructions</Label>
                      <Select 
                        value={newInvestment.maturityInstructions}
                        onValueChange={(value) => setNewInvestment({...newInvestment, maturityInstructions: value})}
                      >
                        <SelectTrigger id="maturityInstructions">
                          <SelectValue placeholder="Select instructions" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="principal_and_interest">Withdraw Principal & Interest</SelectItem>
                          <SelectItem value="principal_only">Withdraw Principal Only</SelectItem>
                          <SelectItem value="reinvest_all">Reinvest Principal & Interest</SelectItem>
                          <SelectItem value="reinvest_principal">Reinvest Principal Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}

                {newInvestment.type === 'mf' && (
                  <div className="space-y-2">
                    <Label htmlFor="expectedReturn">Expected Annual Returns (%)</Label>
                    <Input 
                      id="expectedReturn" 
                      type="number" 
                      step="0.01"
                      value={newInvestment.interestRate} 
                      onChange={(e) => setNewInvestment({...newInvestment, interestRate: parseFloat(e.target.value)})}
                      readOnly
                    />
                    <p className="text-sm text-muted-foreground">
                      Based on recent fund performance (indicative only)
                    </p>
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="startDate">Investment Date</Label>
                  <Input 
                    id="startDate" 
                    type="date" 
                    value={newInvestment.startDate} 
                    onChange={(e) => setNewInvestment({...newInvestment, startDate: e.target.value})}
                  />
                </div>
                
                {newInvestment.type === 'fd' && (
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <h4 className="font-medium mb-2">Investment Summary</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>Principal Amount:</div>
                      <div className="text-right">₹{(newInvestment.amount).toLocaleString('en-IN')}</div>
                      
                      <div>Interest Rate:</div>
                      <div className="text-right">{newInvestment.interestRate}% p.a.</div>
                      
                      <div>Tenor:</div>
                      <div className="text-right">{newInvestment.tenor} months</div>
                      
                      <div>Maturity Date:</div>
                      <div className="text-right">
                        {format(
                          new Date(new Date(newInvestment.startDate).setMonth(
                            new Date(newInvestment.startDate).getMonth() + newInvestment.tenor
                          )), 
                          'dd MMM yyyy'
                        )}
                      </div>
                      
                      <div>Estimated Interest:</div>
                      <div className="text-right text-green-600">
                        ₹{Math.round(newInvestment.amount * (newInvestment.interestRate / 100) * (newInvestment.tenor / 12)).toLocaleString('en-IN')}
                      </div>
                      
                      <div>Maturity Value:</div>
                      <div className="text-right font-medium">
                        ₹{Math.round(newInvestment.amount + (newInvestment.amount * (newInvestment.interestRate / 100) * (newInvestment.tenor / 12))).toLocaleString('en-IN')}
                      </div>
                    </div>
                  </div>
                )}
                
                {newInvestment.type === 'mf' && (
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <h4 className="font-medium mb-2">Investment Summary</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>Principal Amount:</div>
                      <div className="text-right">₹{(newInvestment.amount).toLocaleString('en-IN')}</div>
                      
                      <div>Expected Annual Return:</div>
                      <div className="text-right">{newInvestment.interestRate}% p.a.</div>
                      
                      <div>Investment Type:</div>
                      <div className="text-right">Open-Ended</div>
                      
                      <div>Monthly Expected Return:</div>
                      <div className="text-right text-green-600">
                        ₹{Math.round(newInvestment.amount * (newInvestment.interestRate / 100) / 12).toLocaleString('en-IN')}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex justify-end">
              <Button 
                onClick={handleNewInvestment}
                disabled={!newInvestment.institution || operationSuccess}
              >
                {operationSuccess ? "Investment Created!" : "Create Investment"}
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
        
        <TabsContent value="redeem" className="space-y-4 pt-4">
          <Card>
            <CardHeader>
              <CardTitle>Investment Redemption</CardTitle>
              <CardDescription>
                Redeem investments to meet liquidity requirements
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="selectInvestment">Select Investment</Label>
                  <Select 
                    value={redemption.id}
                    onValueChange={(value) => {
                      const selectedInv = availableInvestments.find(inv => inv.id === value);
                      setRedemption({
                        ...redemption, 
                        id: value,
                        amount: selectedInv ? selectedInv.principal : 0,
                        investmentType: selectedInv?.type.toLowerCase().includes('deposit') ? 'fd' : 'mf'
                      });
                    }}
                  >
                    <SelectTrigger id="selectInvestment">
                      <SelectValue placeholder="Select an investment to redeem" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableInvestments.map((inv) => (
                        <SelectItem key={inv.id} value={inv.id}>
                          {`${inv.institution} - ₹${(inv.principal / 100000).toFixed(2)} lakhs`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {redemption.id && (
                  <>
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <h4 className="font-medium mb-2">Investment Details</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>Institution:</div>
                        <div className="text-right">{availableInvestments.find(inv => inv.id === redemption.id)?.institution}</div>
                        
                        <div>Type:</div>
                        <div className="text-right">{availableInvestments.find(inv => inv.id === redemption.id)?.type}</div>
                        
                        <div>Principal Amount:</div>
                        <div className="text-right">₹{(availableInvestments.find(inv => inv.id === redemption.id)?.principal || 0).toLocaleString('en-IN')}</div>
                        
                        {redemption.investmentType === 'fd' && (
                          <>
                            <div>Maturity Date:</div>
                            <div className="text-right">
                              {format(new Date(availableInvestments.find(inv => inv.id === redemption.id)?.maturityDate || ''), 'dd MMM yyyy')}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="redemptionAmount">Redemption Amount (₹)</Label>
                      <Input 
                        id="redemptionAmount" 
                        type="number" 
                        value={redemption.amount} 
                        onChange={(e) => setRedemption({...redemption, amount: parseFloat(e.target.value)})}
                      />
                      <p className="text-sm text-muted-foreground">
                        {`₹${(redemption.amount / 100000).toFixed(2)} lakhs`}
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="redemptionDate">Redemption Date</Label>
                      <Input 
                        id="redemptionDate" 
                        type="date" 
                        value={redemption.redemptionDate} 
                        onChange={(e) => setRedemption({...redemption, redemptionDate: e.target.value})}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="redemptionReason">Redemption Reason</Label>
                      <Select 
                        value={redemption.reason}
                        onValueChange={(value) => setRedemption({...redemption, reason: value})}
                      >
                        <SelectTrigger id="redemptionReason">
                          <SelectValue placeholder="Select reason for redemption" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="liquidity_needs">Liquidity Needs</SelectItem>
                          <SelectItem value="better_rate">Better Rate Elsewhere</SelectItem>
                          <SelectItem value="policy_change">Policy Change</SelectItem>
                          <SelectItem value="market_volatility">Market Volatility</SelectItem>
                          <SelectItem value="asset_reallocation">Asset Reallocation</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {redemption.investmentType === 'fd' && (
                      <div className="space-y-2">
                        <Label htmlFor="penalties">Early Redemption Penalties (₹)</Label>
                        <Input 
                          id="penalties" 
                          type="number" 
                          value={redemption.penalties} 
                          onChange={(e) => setRedemption({...redemption, penalties: parseFloat(e.target.value)})}
                        />
                        <p className="text-sm text-amber-500">
                          Premature redemption will incur interest rate reduction as per bank terms
                        </p>
                      </div>
                    )}
                    
                    <div className="p-4 bg-muted/50 rounded-lg">
                      <h4 className="font-medium mb-2">Redemption Summary</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div>Redemption Amount:</div>
                        <div className="text-right">₹{redemption.amount.toLocaleString('en-IN')}</div>
                        
                        {redemption.investmentType === 'fd' && (
                          <>
                            <div>Penalties (if any):</div>
                            <div className="text-right text-amber-500">₹{redemption.penalties.toLocaleString('en-IN')}</div>
                            
                            <div>Net Redemption Value:</div>
                            <div className="text-right font-medium">₹{(redemption.amount - redemption.penalties).toLocaleString('en-IN')}</div>
                          </>
                        )}
                        
                        <div>Redemption Date:</div>
                        <div className="text-right">{format(new Date(redemption.redemptionDate), 'dd MMM yyyy')}</div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
            <CardFooter className="flex justify-end">
              <Button 
                onClick={handleRedemptionRequest}
                disabled={!redemption.id || operationSuccess}
              >
                {operationSuccess ? "Redemption Initiated!" : "Request Redemption"}
              </Button>
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Confirmation Dialog for New Investment */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Investment</DialogTitle>
            <DialogDescription>
              Please review the investment details before proceeding.
            </DialogDescription>
          </DialogHeader>
          {newInvestment.institution && (
            <div className="space-y-4 py-4">
              <div className="flex items-center space-x-2">
                {newInvestment.type === 'fd' ? 
                  <Banknote className="h-5 w-5 text-primary" /> : 
                  <PiggyBank className="h-5 w-5 text-primary" />
                }
                <span className="font-medium">
                  {newInvestment.type === 'fd' ? 'Fixed Deposit' : 'Liquid Mutual Fund'}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div className="font-medium">Amount:</div>
                <div>₹{newInvestment.amount.toLocaleString('en-IN')}</div>
                
                <div className="font-medium">{newInvestment.type === 'fd' ? 'Bank:' : 'Fund:'}</div>
                <div>{newInvestment.institution}</div>
                
                <div className="font-medium">{newInvestment.type === 'fd' ? 'Interest Rate:' : 'Expected Return:'}</div>
                <div>{newInvestment.interestRate}% p.a.</div>
                
                {newInvestment.type === 'fd' && (
                  <>
                    <div className="font-medium">Tenor:</div>
                    <div>{newInvestment.tenor} months</div>
                    
                    <div className="font-medium">Maturity Instructions:</div>
                    <div>
                      {newInvestment.maturityInstructions === 'principal_and_interest' && 'Withdraw Principal & Interest'}
                      {newInvestment.maturityInstructions === 'principal_only' && 'Withdraw Principal Only'}
                      {newInvestment.maturityInstructions === 'reinvest_all' && 'Reinvest Principal & Interest'}
                      {newInvestment.maturityInstructions === 'reinvest_principal' && 'Reinvest Principal Only'}
                    </div>
                  </>
                )}
                
                <div className="font-medium">Investment Date:</div>
                <div>{format(new Date(newInvestment.startDate), 'dd MMM yyyy')}</div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>Cancel</Button>
            <Button onClick={confirmInvestment}>Confirm Investment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog for Redemption */}
      <Dialog open={showRedeemDialog} onOpenChange={setShowRedeemDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Redemption</DialogTitle>
            <DialogDescription>
              Please review the redemption details before proceeding.
            </DialogDescription>
          </DialogHeader>
          {redemption.id && (
            <div className="space-y-4 py-4">
              <div className="flex items-center space-x-2">
                {redemption.investmentType === 'fd' ? 
                  <Banknote className="h-5 w-5 text-primary" /> : 
                  <PiggyBank className="h-5 w-5 text-primary" />
                }
                <span className="font-medium">
                  {availableInvestments.find(inv => inv.id === redemption.id)?.type}
                </span>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div className="font-medium">Institution:</div>
                <div>{availableInvestments.find(inv => inv.id === redemption.id)?.institution}</div>
                
                <div className="font-medium">Redemption Amount:</div>
                <div>₹{redemption.amount.toLocaleString('en-IN')}</div>
                
                <div className="font-medium">Redemption Date:</div>
                <div>{format(new Date(redemption.redemptionDate), 'dd MMM yyyy')}</div>
                
                <div className="font-medium">Reason:</div>
                <div>
                  {redemption.reason === 'liquidity_needs' && 'Liquidity Needs'}
                  {redemption.reason === 'better_rate' && 'Better Rate Elsewhere'}
                  {redemption.reason === 'policy_change' && 'Policy Change'}
                  {redemption.reason === 'market_volatility' && 'Market Volatility'}
                  {redemption.reason === 'asset_reallocation' && 'Asset Reallocation'}
                </div>
                
                {redemption.investmentType === 'fd' && redemption.penalties > 0 && (
                  <>
                    <div className="font-medium">Penalties:</div>
                    <div className="text-amber-500">₹{redemption.penalties.toLocaleString('en-IN')}</div>
                    
                    <div className="font-medium">Net Redemption Value:</div>
                    <div>₹{(redemption.amount - redemption.penalties).toLocaleString('en-IN')}</div>
                  </>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRedeemDialog(false)}>Cancel</Button>
            <Button onClick={confirmRedemption}>Confirm Redemption</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvestmentOperations;
